import container from "@/lib/di/container";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { TransactionRepo } from "@/core/repos/tables/transaction.repo";
import { TopUpTransactionRepo } from "@/core/repos/tables/top-up-transaction.repo";
import { ReadMeterPeriodRepo } from "@/core/repos/tables/read-meter-period.repo";
import { UserRequestRepo } from "@/core/repos/tables/user-request.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { WaterRegistrationRepo } from "@/core/repos/tables/water-registration.repo";
import { SampleTestRepo } from "@/core/repos/tables/sample-test.repo";
import { WaterMeterRepo } from "@/core/repos/tables/water-meter.repo";
import { FeeLadderRepo } from "@/core/repos/tables/fee-ladder.repo";
import { FeeLadderLevelRepo } from "@/core/repos/tables/fee-ladder-level.repo";
import { DepartmentRepo } from "@/core/repos/tables/department.repo";
import { WaterSupplySuspensionRepo } from "@/core/repos/tables/water-supply-suspension.repo";
import { PaymentPointRepo } from "@/core/repos/tables/payment-point.repo";

export interface ToolSchema {
  type: "function";
  function: {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  };
}

export type ToolHandler = (args: Record<string, unknown>) => Promise<unknown>;

const MAX_ROWS = 20;

function trimPage(
  page: { data: any[]; total: number } | null | undefined,
  keys: string[]
) {
  if (!page) return { empty: true, total: 0, data: [] };
  const data = (page.data ?? []).slice(0, MAX_ROWS).map((row: any) => {
    const out: Record<string, unknown> = {};
    for (const k of keys) out[k] = row?.[k];
    return out;
  });
  return { total: page.total ?? data.length, returned: data.length, data };
}

function errWrap<T>(fn: () => Promise<T>) {
  return fn().catch((err: unknown) => ({
    error: err instanceof Error ? err.message : String(err),
  }));
}

function splitPageSize(args: Record<string, unknown>): {
  params: any;
  pageSize: number;
} {
  const { pageSize, ...rest } = args;
  return { params: rest, pageSize: Number(pageSize ?? 10) };
}

function triggerDownload(blob: unknown, filename: string): boolean {
  if (!blob) return false;
  try {
    const url = URL.createObjectURL(blob as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}

function timestamp() {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(
    d.getHours()
  )}${pad(d.getMinutes())}`;
}

export const TOOL_SCHEMAS: ToolSchema[] = [
  // ========== KHÁCH HÀNG ==========
  {
    type: "function",
    function: {
      name: "search_customers",
      description:
        "Tìm kiếm khách hàng (hợp đồng cấp nước) theo tên/mã/SĐT/CMND/mã đồng hồ.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          phone: { type: "string" },
          identityNo: { type: "string" },
          meterCode: { type: "string" },
          tollAreaId: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_customer_detail",
      description: "Chi tiết 1 khách hàng theo id hợp đồng cấp nước.",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_customer_deposit_summary",
      description:
        "Thống kê khách hàng theo tình trạng đặt cọc / công nợ trong khoảng thời gian.",
      parameters: {
        type: "object",
        properties: {
          fromDate: { type: "string" },
          toDate: { type: "string" },
          tollAreaId: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "count_customers",
      description: "Đếm tổng số khách hàng theo bộ lọc.",
      parameters: {
        type: "object",
        properties: {
          tollAreaId: { type: "string" },
          status: { type: "string" },
          hasMeter: { type: "boolean" },
          depositStatus: { type: "string" },
        },
      },
    },
  },

  // ========== CHỈ SỐ NƯỚC ==========
  {
    type: "function",
    function: {
      name: "list_water_index",
      description: "Tra danh sách chỉ số nước theo tháng/năm/khu vực.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          month: { type: "number" },
          year: { type: "number" },
          tollAreaId: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_customer_water_index_history",
      description:
        "Lịch sử toàn bộ chỉ số nước của 1 khách hàng (dùng waterUserId).",
      parameters: {
        type: "object",
        properties: { waterUserId: { type: "string" } },
        required: ["waterUserId"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_customer_water_index_for_period",
      description:
        "Lấy chỉ số nước của 1 khách hàng cho 1 tháng/năm cụ thể.",
      parameters: {
        type: "object",
        properties: {
          waterUserId: { type: "string" },
          year: { type: "number" },
          month: { type: "number" },
        },
        required: ["waterUserId", "year", "month"],
      },
    },
  },

  // ========== HOÁ ĐƠN ==========
  {
    type: "function",
    function: {
      name: "list_water_invoices",
      description: "Danh sách hoá đơn nước theo bộ lọc.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          month: { type: "number" },
          year: { type: "number" },
          approveStatus: { type: "string" },
          waterUserId: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_invoice_summary",
      description:
        "Thống kê hoá đơn theo tháng/năm, tách theo trạng thái Paid (đã thu) và Unpaid (chưa thu). Trả về: số hoá đơn, lượng nước m³, tiền sử dụng nước, thuế VAT, phí BVMT, giảm giá, tổng thanh toán. KÈM đối tượng tongCong cộng dồn tất cả trạng thái. Dùng khi hỏi doanh thu, tổng hoá đơn, đã thu/chưa thu theo tháng. KHÔNG dùng cho: danh sách khu vực thu phí, mã khu, 'có những khu nào' — các câu đó dùng list_toll_areas hoặc list_tollareas_by_ward.",
      parameters: {
        type: "object",
        properties: {
          month: { type: "number", description: "Tháng (1-12)" },
          year: { type: "number", description: "Năm (yyyy)" },
          tollAreaId: { type: "string" },
        },
        required: ["year"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_invoice_qr",
      description: "Lấy QR hoá đơn điện tử của 1 hoá đơn.",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_invoice_vnpay_qr",
      description:
        "Lấy QR VNPay để thanh toán 1 hoá đơn (trả base64 PNG).",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
    },
  },

  // ========== GIAO DỊCH ==========
  {
    type: "function",
    function: {
      name: "list_transactions",
      description: "Lịch sử giao dịch thu tiền.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_topup_transactions",
      description: "Lịch sử nạp tiền vào tài khoản khách hàng.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },

  // ========== KỲ GHI CHỈ SỐ / YÊU CẦU ==========
  {
    type: "function",
    function: {
      name: "list_read_meter_periods",
      description: "Danh sách kỳ ghi chỉ số nước.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_user_requests",
      description: "Danh sách yêu cầu / phản ánh từ khách hàng.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_user_request_detail",
      description: "Chi tiết 1 yêu cầu khách hàng, kèm timeline xử lý.",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_user_request_enums",
      description:
        "Danh sách enum (trạng thái, loại) cho yêu cầu khách hàng.",
      parameters: { type: "object", properties: {} },
    },
  },

  // ========== KHU VỰC / THỐNG KÊ ==========
  {
    type: "function",
    function: {
      name: "list_toll_areas",
      description:
        "Danh sách khu vực thu phí của nhà máy (id, tên, mã). Không tham số. BẮT BUỘC gọi tool này khi người dùng hỏi danh sách/liệt kê khu vực, mã khu, 'có những khu nào', 'khu vực của nhà máy' — không được tự bịa bảng. Cũng dùng để map tên khu → id khi cần. KHÔNG dùng get_invoice_summary hay get_toll_area_statistics chỉ để lấy danh sách tên/mã khu.",
      parameters: { type: "object", properties: {} },
    },
  },
  {
    type: "function",
    function: {
      name: "list_tollareas_by_ward",
      description:
        "Danh sách khu vực thu phí theo một phường/xã cụ thể (cần wardId). Chỉ dùng khi đã có hoặc user cho mã/id phường. KHÔNG thay thế list_toll_areas cho câu 'danh sách toàn bộ khu vực nhà máy' (không lọc phường).",
      parameters: {
        type: "object",
        properties: { wardId: { type: "string" } },
        required: ["wardId"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_toll_area_statistics",
      description:
        "THỐNG KÊ TỔNG HỢP theo khu vực thu phí (theo tháng/năm và bộ lọc): số khách hàng, số cài app, tổng lượng nước, doanh thu, đã thu, chưa thu, số hoá đơn... Dùng cho câu hỏi thống kê đa chỉ tiêu theo khu + thời gian. Khi trả lời người dùng, tên khu và mọi con số PHẢI lấy từ field trong mảng data trả về — không bịa tên kiểu Khu A/B/C. KHÔNG dùng nếu chỉ cần danh sách tên/mã khu vực (không cần số liệu thống kê) — khi đó chỉ dùng list_toll_areas.",
      parameters: {
        type: "object",
        properties: {
          month: { type: "string", description: "Tháng (1-12)" },
          year: { type: "string", description: "Năm (yyyy)" },
          provinceId: { type: "string" },
          districtId: { type: "string" },
          wardId: { type: "string" },
          tollAreaId: { type: "number" },
          search: { type: "string" },
        },
      },
    },
  },

  // ========== DANH MỤC BỔ TRỢ ==========
  {
    type: "function",
    function: {
      name: "list_payment_points",
      description: "Danh sách điểm thu tiền.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_water_registrations",
      description: "Danh sách đơn đăng ký cấp nước của khách mới.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_sample_tests",
      description: "Danh sách kết quả xét nghiệm mẫu nước.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_water_meters",
      description: "Danh sách đồng hồ nước.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_fee_ladders",
      description: "Danh sách biểu giá bậc thang.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_fee_ladder_levels",
      description: "Chi tiết các bậc giá trong biểu giá bậc thang.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          feeLadderId: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_departments",
      description: "Danh sách phòng ban / đơn vị.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_suspensions",
      description: "Danh sách lịch tạm ngưng cấp nước.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          pageSize: { type: "number", default: 10 },
        },
      },
    },
  },

  // ========== EXPORT / TẢI FILE ==========
  {
    type: "function",
    function: {
      name: "export_customers_excel",
      description:
        "Xuất Excel danh sách khách hàng theo bộ lọc. File sẽ được tải xuống máy người dùng tự động.",
      parameters: {
        type: "object",
        properties: {
          search: { type: "string" },
          tollAreaId: { type: "string" },
          status: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "export_water_index_excel",
      description: "Xuất Excel chỉ số nước theo tháng/năm/khu vực.",
      parameters: {
        type: "object",
        properties: {
          month: { type: "number" },
          year: { type: "number" },
          tollAreaId: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "export_invoices_excel",
      description: "Xuất Excel danh sách hoá đơn theo bộ lọc.",
      parameters: {
        type: "object",
        properties: {
          month: { type: "number" },
          year: { type: "number" },
          approveStatus: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "export_invoice_report",
      description: "Xuất báo cáo tổng hợp hoá đơn (Excel).",
      parameters: {
        type: "object",
        properties: {
          month: { type: "number" },
          year: { type: "number" },
          tollAreaId: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "export_transaction_report",
      description: "Xuất báo cáo thu tiền / giao dịch (Excel).",
      parameters: {
        type: "object",
        properties: {
          fromDate: { type: "string" },
          toDate: { type: "string" },
          tollAreaId: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "export_customer_contract",
      description: "Xuất hợp đồng cấp nước của 1 khách hàng (Word/PDF).",
      parameters: {
        type: "object",
        properties: { contractId: { type: "string" } },
        required: ["contractId"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "export_contracts_by_tollarea",
      description: "Xuất toàn bộ hợp đồng của 1 khu vực thu phí.",
      parameters: {
        type: "object",
        properties: { tollAreaId: { type: "string" } },
        required: ["tollAreaId"],
      },
    },
  },
];

export const TOOL_HANDLERS: Record<string, ToolHandler> = {
  // ========== KHÁCH HÀNG ==========
  search_customers: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "code",
        "name",
        "phone",
        "address",
        "tollAreaName",
        "unitTypeName",
        "status",
      ]);
    }),

  get_customer_detail: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const detail = await repo.detail(String(args.id));
      return detail ?? { empty: true };
    }),

  get_customer_deposit_summary: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const data = await repo.getSummary(args);
      if (!data || data.length === 0) {
        return { empty: true };
      }
      return { total: data.length, data };
    }),

  count_customers: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const page = await repo.getData(1, args as any, 1);
      return { total: page?.total ?? 0, filter: args };
    }),

  // ========== CHỈ SỐ NƯỚC ==========
  list_water_index: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterIndexRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "year",
        "month",
        "waterUserId",
        "waterMeterNumber",
        "waterMeterNumberLast",
        "tollAreaId",
      ]);
    }),

  get_customer_water_index_history: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterIndexRepo);
      const list = await repo.allByWaterUser(String(args.waterUserId));
      return {
        total: list.length,
        data: list.slice(0, 24).map((r: any) => ({
          year: r.year,
          month: r.month,
          waterMeterNumber: r.waterMeterNumber,
          waterMeterNumberLast: r.waterMeterNumberLast,
          consumption:
            (r.waterMeterNumber ?? 0) - (r.waterMeterNumberLast ?? 0),
        })),
      };
    }),

  get_customer_water_index_for_period: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterIndexRepo);
      const data = await repo.getByWaterUserAndPeriod(
        String(args.waterUserId),
        Number(args.year),
        Number(args.month)
      );
      return data ?? { empty: true };
    }),

  // ========== HOÁ ĐƠN ==========
  list_water_invoices: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterInvoiceRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "code",
        "year",
        "month",
        "total",
        "waterUserId",
        "approveStatus",
      ]);
    }),

  get_invoice_summary: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterInvoiceRepo);
      const raw = await repo.getSummary(args);
      if (!raw || raw.length === 0) {
        return {
          empty: true,
          hint: "Không có hoá đơn trong kỳ này.",
        };
      }
      const rows = raw.map((r: any) => ({
        trangThai: r.status,
        soHoaDon: r.statusAmount,
        luongNuocM3: r.amount,
        tienSuDungNuoc: r.useTotal,
        thueVAT: r.vatTotal,
        phiBaoVeMoiTruong: r.enviromentFeeTotal,
        tongTruocGiamGia: r.allTotal,
        giamGia: r.discount,
        tongThanhToan: r.total,
      }));
      const sum = (k: keyof (typeof rows)[number]) =>
        rows.reduce((s: number, r: any) => s + (Number(r[k]) || 0), 0);
      return {
        filter: args,
        fieldGuide: {
          soHoaDon: "Số lượng hoá đơn",
          luongNuocM3: "Tổng lượng nước sử dụng (m³)",
          tienSuDungNuoc: "Tiền sử dụng nước (trước thuế, trước giảm giá)",
          thueVAT: "Thuế VAT",
          phiBaoVeMoiTruong: "Phí bảo vệ môi trường",
          tongTruocGiamGia:
            "Tổng tiền trước giảm giá = tienSuDungNuoc + thueVAT + phiBaoVeMoiTruong",
          giamGia: "Giảm giá",
          tongThanhToan:
            "Tổng tiền phải thanh toán = tongTruocGiamGia - giamGia",
        },
        byStatus: rows,
        tongCong: {
          soHoaDon: sum("soHoaDon"),
          luongNuocM3: sum("luongNuocM3"),
          tienSuDungNuoc: sum("tienSuDungNuoc"),
          thueVAT: sum("thueVAT"),
          phiBaoVeMoiTruong: sum("phiBaoVeMoiTruong"),
          tongTruocGiamGia: sum("tongTruocGiamGia"),
          giamGia: sum("giamGia"),
          tongThanhToan: sum("tongThanhToan"),
        },
      };
    }),

  get_invoice_qr: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterInvoiceRepo);
      const qr = await repo.getQR({ id: String(args.id) } as any);
      return qr ? { qr } : { empty: true };
    }),

  get_invoice_vnpay_qr: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterInvoiceRepo);
      const qr = await repo.getVNPayQR({ id: String(args.id) } as any);
      return qr
        ? { qrImage: qr, hint: "Base64 PNG, có thể hiển thị trong chat." }
        : { empty: true };
    }),

  // ========== GIAO DỊCH ==========
  list_transactions: (args) =>
    errWrap(async () => {
      const repo = container.get(TransactionRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "objectType",
        "amount",
        "createdAt",
        "note",
      ]);
    }),

  list_topup_transactions: (args) =>
    errWrap(async () => {
      const repo = container.get(TopUpTransactionRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "amount", "objectType", "createdAt"]);
    }),

  // ========== KỲ GHI / YÊU CẦU ==========
  list_read_meter_periods: (args) =>
    errWrap(async () => {
      const repo = container.get(ReadMeterPeriodRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "waterFactoryId",
        "startDate",
        "endDate",
        "status",
      ]);
    }),

  list_user_requests: (args) =>
    errWrap(async () => {
      const repo = container.get(UserRequestRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "code", "name", "status", "createdAt"]);
    }),

  get_user_request_detail: (args) =>
    errWrap(async () => {
      const repo = container.get(UserRequestRepo);
      const data = await repo.getDetail(String(args.id));
      return data ?? { empty: true };
    }),

  get_user_request_enums: () =>
    errWrap(async () => {
      const repo = container.get(UserRequestRepo);
      const data = await repo.getEnums();
      return data ?? { empty: true };
    }),

  // ========== KHU VỰC / THỐNG KÊ ==========
  list_toll_areas: () =>
    errWrap(async () => {
      const repo = container.get(TollAreaRepo);
      const all = await repo.getAll();
      return {
        total: all.length,
        data: all.slice(0, 100).map((a) => ({
          id: a.id,
          name: a.name,
          code: a.code,
        })),
      };
    }),

  list_tollareas_by_ward: (args) =>
    errWrap(async () => {
      const repo = container.get(TollAreaRepo);
      const all = await repo.getAllByWard(String(args.wardId));
      return {
        total: all.length,
        data: all.slice(0, 100).map((a: any) => ({
          id: a.id,
          name: a.name,
          code: a.code,
        })),
      };
    }),

  get_toll_area_statistics: (args) =>
    errWrap(async () => {
      const repo = container.get(TollAreaRepo);
      const data = await repo.getStatistics(args as any);
      if (!data || data.length === 0) return { empty: true };
      // Loại bỏ field đặt cọc (tongTienPhaiDong/tongTienDaDong) vì đó là
      // tiền cọc đăng ký cấp nước, không liên quan doanh thu nhà máy.
      const cleaned = data.slice(0, 50).map((r: any) => {
        const {
          tongTienPhaiDong: _a,
          tongTienDaDong: _b,
          ...rest
        } = r;
        void _a;
        void _b;
        return rest;
      });
      return { total: data.length, data: cleaned };
    }),

  // ========== DANH MỤC BỔ TRỢ ==========
  list_payment_points: (args) =>
    errWrap(async () => {
      const repo = container.get(PaymentPointRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "name", "address", "phone"]);
    }),

  list_water_registrations: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterRegistrationRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "name",
        "phone",
        "address",
        "status",
        "createdAt",
      ]);
    }),

  list_sample_tests: (args) =>
    errWrap(async () => {
      const repo = container.get(SampleTestRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "name", "sendDate", "status"]);
    }),

  list_water_meters: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterMeterRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "code", "qrCode", "status"]);
    }),

  list_fee_ladders: (args) =>
    errWrap(async () => {
      const repo = container.get(FeeLadderRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "name", "status", "provinceName"]);
    }),

  list_fee_ladder_levels: (args) =>
    errWrap(async () => {
      const repo = container.get(FeeLadderLevelRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, [
        "id",
        "name",
        "fromAmount",
        "toAmount",
        "price",
        "feeLadderId",
      ]);
    }),

  list_departments: (args) =>
    errWrap(async () => {
      const repo = container.get(DepartmentRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "name", "code"]);
    }),

  list_suspensions: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterSupplySuspensionRepo);
      const { params, pageSize } = splitPageSize(args);
      const page = await repo.getData(1, params, pageSize);
      return trimPage(page, ["id", "name", "status", "startDate", "endDate"]);
    }),

  // ========== EXPORT ==========
  export_customers_excel: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const blob = await repo.exportExcel(args);
      const filename = `khach-hang-${timestamp()}.xlsx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tạo được file Excel" };
    }),

  export_water_index_excel: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterIndexRepo);
      const blob = await repo.exportExcel(args);
      const filename = `chi-so-nuoc-${timestamp()}.xlsx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tạo được file Excel" };
    }),

  export_invoices_excel: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterInvoiceRepo);
      const blob = await repo.exportExcelAll(args);
      const filename = `hoa-don-${timestamp()}.xlsx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tạo được file Excel" };
    }),

  export_invoice_report: (args) =>
    errWrap(async () => {
      const repo = container.get(WaterInvoiceRepo);
      const blob = await repo.exportReport(args);
      const filename = `bao-cao-hoa-don-${timestamp()}.xlsx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tạo được báo cáo" };
    }),

  export_transaction_report: (args) =>
    errWrap(async () => {
      const repo = container.get(TransactionRepo);
      const blob = await repo.exportReport(args);
      const filename = `bao-cao-thu-tien-${timestamp()}.xlsx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tạo được báo cáo" };
    }),

  export_customer_contract: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const blob = await repo.exportContract(String(args.contractId));
      const filename = `hop-dong-${args.contractId}-${timestamp()}.docx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tải được hợp đồng" };
    }),

  export_contracts_by_tollarea: (args) =>
    errWrap(async () => {
      const repo = container.get(ContractRepo);
      const blob = await repo.exportContractByTollArea(
        String(args.tollAreaId)
      );
      const filename = `hop-dong-khu-vuc-${timestamp()}.docx`;
      const ok = triggerDownload(blob, filename);
      return ok
        ? { downloaded: true, filename }
        : { error: "Không tải được" };
    }),
};
